import { useEffect, useRef, useState } from 'react';
import { DataStatus, isIdle, isLoading } from '../lib';

interface DataFetcherInfo<T> {
  data?: T;
  idle: boolean;
  loading: boolean;
  error?: unknown;
  dataStatus: DataStatus;
  retry: () => void;
}

interface DataFetcherOptions {
  keepPreviousData?: boolean;
}

export const useDataFetcher = <T>(
  fetcher: () => Promise<T>,
  { keepPreviousData = false }: DataFetcherOptions = {},
): DataFetcherInfo<T> => {
  const [dataStatus, setDataStatus] = useState(DataStatus.idle);
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>();
  const latestFetcher = useRef<() => Promise<T>>();

  const requestData = () => {
    setDataStatus(DataStatus.loading);
    setError(undefined);
    fetcher()
      .then((response) => {
        if (fetcher !== latestFetcher.current) {
          return;
        }

        setData(response);
        setDataStatus(DataStatus.finished);
      })
      .catch((error) => {
        if (fetcher !== latestFetcher.current) {
          return;
        }

        setError(error);
        setDataStatus(DataStatus.error);
      });
  };

  useEffect(
    () => {
      latestFetcher.current = fetcher;
      requestData();

      return () => {
        if (!keepPreviousData) {
          setData(undefined);
        }

        setDataStatus(DataStatus.idle);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetcher],
  );

  return {
    idle: isIdle(dataStatus),
    data,
    loading: isLoading(dataStatus),
    error,
    dataStatus,
    retry: requestData,
  };
};
